import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { storableError } from '../../util/errors';
import { denormalisedResponseEntities } from '../../util/data';

export const addToCart = createAsyncThunk('cart/add', (params, { extra: sdk }) =>
  sdk.newSdk.cartListings.create({
    listingId: params.listingId,
    amount: params.amount,
    start: params.start,
    end: params.end,
  })
);

export const deleteFromCart = createAsyncThunk('cart/delete', (listingId, { extra: sdk }) =>
  sdk.newSdk.cartListings.delete({ id: listingId })
);

export const updateCartListing = createAsyncThunk(
  'cart/updateCartListing',
  ({ cartListingId, amount }, { extra: sdk }) =>
    sdk.newSdk.cartListings.update({ id: cartListingId, amount })
);

export const addCartToUser = createAsyncThunk(
  'cart/addCartToUser',
  ({ cartId, email }, { extra: sdk }) =>
    sdk.newSdk.cartPreorders.update({
      id: cartId,
      clientEmail: email,
    })
);

export const fetchCartPreorders = createAsyncThunk(
  'cart/fetchCartPreorders',
  async (_, { dispatch, extra: sdk }) => {
    if (typeof window === 'undefined') return { data: { data: [], included: [], meta: {} } };
    const queryParams = {
      include: [
        'cartListings',
        'cartListings.listing',
        'provider',
        'cartListings.listing.images',
        'provider.deliveryToProviders',
      ],
    };
    const response = await sdk.newSdk.cartPreorders.query(queryParams);
    dispatch(addMarketplaceEntities(response));
    return response;
  }
);

export const fetchPendingServiceFees = createAsyncThunk('cart/fetchFees', (_, { extra: sdk }) =>
  sdk.newSdk.transactions.query({
    serviceFee: true,
    state: 'pending',
    userId: 'me',
    include: ['relatedTransactions.listings.images', 'provider'],
  })
);

const timeSlotsRequest = createAsyncThunk(
  'cart/timeSlotsRequest',
  async (params, { extra: sdk }) => {
    const response = await sdk.newSdk.timeSlots.query(params);
    return denormalisedResponseEntities(response);
  }
);

export const fetchTimeSlots = createAsyncThunk(
  'cart/fetchTimeSlots',
  async ({ listingId, start, end }, { dispatch, rejectWithValue }) => {
    try {
      const params = { listingId: listingId.uuid, start, end };
      return await dispatch(timeSlotsRequest(params)).unwrap();
    } catch (error) {
      return rejectWithValue(storableError(error));
    }
  }
);

const subtractOneDay = dateString => {
  if (!dateString) return dateString;
  const date = new Date(dateString);
  date.setDate(date.getDate() - 1);
  return date.toISOString().split('T')[0];
};

const formatPreorders = payload => {
  const { data = [], included = [] } = payload?.data || {};

  return data.map(item => {
    const providerRef = item.relationships?.provider?.data;
    const provider = providerRef ? included.find(i => i.id?.uuid === providerRef.id?.uuid) : null;

    const cartListingsRef = item.relationships?.cartListings?.data || [];
    const cartListings = cartListingsRef.map(clRef => {
      const cartListing = included.find(i => i.id?.uuid === clRef.id?.uuid);

      const listingRef = cartListing?.relationships?.listing?.data;
      const listing = listingRef ? included.find(i => i.id?.uuid === listingRef.id?.uuid) : null;

      return {
        ...cartListing,
        listing,
      };
    });

    return {
      ...item,
      provider,
      cartListings,
    };
  });
};

export const fetchCartData = createAsyncThunk(
  'cart/fetchCartData',
  async (params, { dispatch, rejectWithValue }) => {
    try {
      const preorders = await dispatch(fetchCartPreorders(params)).unwrap();
      const preorderItems = formatPreorders(preorders);

      const uniqueListingsWithDates = preorderItems
        .flatMap(preorder =>
          (preorder.cartListings || []).map(cartListing => ({
            cartPreorderId: preorder.id.uuid,
            cartListingId: cartListing.id?.uuid,
            listingId: cartListing.listing?.id?.uuid,
            start: cartListing.attributes?.start,
            end: subtractOneDay(cartListing.attributes?.end),
          }))
        )
        .filter(
          (item, index, self) =>
            index ===
            self.findIndex(
              t => t.listingId === item.listingId && t.start === item.start && t.end === item.end
            )
        );

      const timeSlots = await Promise.all(
        uniqueListingsWithDates.map(async item => {
          const param = { listingId: { uuid: item.listingId }, start: item.start, end: item.end };
          const slots = await dispatch(fetchTimeSlots(param)).unwrap();
          return {
            ...param,
            cartPreorderId: item.cartPreorderId,
            cartListingId: item.cartListingId,
            slots,
          };
        })
      );

      return { preorders: preorderItems, timeSlots };
    } catch (error) {
      return rejectWithValue(storableError(error));
    }
  }
);

export const updateCartListingAndRefetchTimeSlot = createAsyncThunk(
  'cart/updateAndRefetchSlot',
  async ({ cartListingId, amount, listingId, start, end }, { dispatch, rejectWithValue }) => {
    try {
      await dispatch(updateCartListing({ cartListingId, amount })).unwrap();
          await dispatch(fetchCartPreorders()).unwrap();

      const slots = await dispatch(
        fetchTimeSlots({ listingId: { uuid: listingId }, start, end: subtractOneDay(end) })
      ).unwrap();

      return { cartListingId, amount, slots };
    } catch (error) {
      return rejectWithValue(storableError(error));
    }
  }
);

export const deleteCartListingAndRemoveLocally = createAsyncThunk(
  'cart/deleteAndRemoveLocally',
  async (listingId, { dispatch, rejectWithValue }) => {
    try {
      await dispatch(deleteFromCart(listingId)).unwrap();
      await dispatch(fetchCartPreorders()).unwrap();
      return listingId;
    } catch (error) {
      return rejectWithValue(storableError(error));
    }
  }
);

export const loadData = () => (dispatch, getState) =>
  Promise.all([
    dispatch(fetchCartData()).unwrap(),
    getState().Auth?.isAuthenticated
      ? dispatch(fetchPendingServiceFees()).unwrap()
      : Promise.resolve(),
  ]);

const initialState = {
  addToCartInProgress: false,
  addToCartError: null,
  addToCartSuccess: false,

  fetchInProgress: false,
  fetchCartSuccess: false,
  fetchCartError: null,

  deleteFromCartInProgress: false,
  deleteFromCartError: null,
  deleteFromCartSuccess: false,
  deletingListingId: null,

  fetchServiceFeesInProgress: false,
  fetchServiceFeesError: null,
  fetchServiceFeesSuccess: false,

  updateCartListingInProgress: false,
  updateCartListingError: null,
  updateCartListingSuccess: false,
  updatingCartListingId: null,

  pagination: null,
  cart: [],
  pendingServiceFeeTransaction: [],
  cartData: {
    timeSlots: [],
  },

  fetchCartDataInProgress: false,
  fetchCartDataError: null,
  
};

const cartSlice = createSlice({
  name: 'cart',
  initialState,
  reducers: {
    clearCartErrors: state => {
      state.addToCartError = null;
      state.deleteFromCartError = null;
      state.updateCartListingError = null;
      state.fetchCartDataError = null;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(addToCart.pending, state => {
        state.addToCartInProgress = true;
        state.addToCartError = null;
        state.addToCartSuccess = false;
      })
      .addCase(addToCart.fulfilled, state => {
        state.addToCartInProgress = false;
        state.addToCartSuccess = true;
      })
      .addCase(addToCart.rejected, (state, action) => {
        state.addToCartInProgress = false;
        state.addToCartError = action.payload || action.error;
      })

      .addCase(deleteFromCart.pending, (state, action) => {
        state.deleteFromCartInProgress = true;
        state.deleteFromCartError = null;
        state.deleteFromCartSuccess = false;
        state.deletingListingId = action.meta.arg;
      })
      .addCase(deleteFromCart.fulfilled, state => {
        state.deleteFromCartInProgress = false;
        state.deleteFromCartSuccess = true;
        state.deletingListingId = null;
      })
      .addCase(deleteFromCart.rejected, (state, action) => {
        state.deleteFromCartInProgress = false;
        state.deleteFromCartError = action.payload || action.error;
        state.deletingListingId = null;
      })

      .addCase(fetchCartPreorders.pending, state => {
        state.fetchInProgress = true;
        state.fetchCartError = null;
      })
      .addCase(fetchCartPreorders.fulfilled, (state, { payload }) => {
        state.fetchInProgress = false;
        state.cart = formatPreorders(payload);
        state.pagination = payload.data.meta;
      })
      .addCase(fetchCartPreorders.rejected, (state, action) => {
        state.fetchInProgress = false;
        state.fetchCartError = action.payload || action.error;
      })

      .addCase(fetchPendingServiceFees.pending, state => {
        state.fetchServiceFeesInProgress = true;
        state.fetchServiceFeesError = null;
        state.fetchServiceFeesSuccess = false;
      })
      .addCase(fetchPendingServiceFees.fulfilled, (state, { payload }) => {
        state.fetchServiceFeesInProgress = false;
        state.fetchServiceFeesSuccess = true;
        const { data = [], included = [] } = payload.data || {};

        state.pendingServiceFeeTransaction = data.map(item => {
          const provider = included.find(
            i => i.id.uuid === item.relationships.provider.data.id.uuid
          );

          const relatedTransactions = item.relationships.relatedTransactions.data.map(tr => {
            const relTr = included.find(i => i.id.uuid === tr.id.uuid);
            const listings = relTr?.relationships.listings.data.map(l => {
              const listing = included.find(el => el.id.uuid === l.id.uuid);
              const images = listing?.relationships.images.data.map(img =>
                included.find(el => el.id.uuid === img.id.uuid)
              );
              return { ...listing, images };
            });

            return { ...tr, type: 'related-transaction', attributes: relTr?.attributes, listings };
          });

          return { ...item, provider, relatedTransactions };
        });
      })
      .addCase(fetchPendingServiceFees.rejected, (state, action) => {
        state.fetchServiceFeesInProgress = false;
        state.fetchServiceFeesError = action.payload || action.error;
      })

      .addCase(fetchCartData.pending, state => {
        state.fetchCartDataInProgress = true;
        state.fetchCartDataError = null;
      })
      .addCase(fetchCartData.fulfilled, (state, action) => {
        state.fetchCartDataInProgress = false;
        state.cartData = action.payload;
        state.cart = action.payload.preorders;
      })
      .addCase(fetchCartData.rejected, (state, action) => {
        state.fetchCartDataInProgress = false;
        state.fetchCartDataError = action.payload || action.error;
      })

      .addCase(updateCartListingAndRefetchTimeSlot.pending, (state, action) => {
        state.updateCartListingInProgress = true;
        state.updateCartListingError = null;
        state.updatingCartListingId = action.meta.arg.cartListingId;
      })
      .addCase(updateCartListingAndRefetchTimeSlot.fulfilled, (state, action) => {
        state.updateCartListingInProgress = false;
        state.updateCartListingSuccess = true;
        state.updatingCartListingId = null;

        const { cartListingId, amount, slots } = action.payload;

        state.cart.forEach(preorder => {
          const listing = preorder.cartListings?.find(cl => cl.id?.uuid === cartListingId);
          if (listing && listing.attributes) {
            listing.attributes.amount = amount;
          }
        });

        if (state.cartData?.timeSlots) {
          state.cartData.timeSlots = state.cartData.timeSlots.map(item =>
            item.cartListingId === cartListingId ? { ...item, slots } : item
          );
        }
      })
      .addCase(updateCartListingAndRefetchTimeSlot.rejected, (state, action) => {
        state.updateCartListingInProgress = false;
        state.updateCartListingError = action.payload || action.error;
        state.updatingCartListingId = null;
      });
  },
});

export const { clearCartErrors } = cartSlice.actions;

export default cartSlice.reducer;
